/**
 * The calculator's answer — the dashboard's, read alike (AC-1):
 *
 *   • the band: one trip from today, the title's limits on absence as meters
 *     under it, and the days still to confirm (settled here by the note below:
 *     the calculator has no record to settle them in);
 *   • "keep" right under it — the one way on, in the host's words (sign up,
 *     start my InOut, continue on Tonnie);
 *   • when the person did not say where they were between the trips, those
 *     days are maybe away: the answer says so, with the way to count them;
 *   • the trips on a line, every rule, the renewal window and the long-term
 *     rights, as on the dashboard;
 *   • "what if I travel?" — one trip simulated on the same record, nothing
 *     saved;
 *   • the answers changed, or the calculation started over.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Bookmark, CircleDot, Plane, RotateCcw } from '@tonnie/assets';
import { inoutDaysToConfirm } from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import { inoutTripFormSchema, type InOutTripFormInput } from '@tonnie/schemas';
import {
  Alert,
  AlertAction,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CountryFlag,
  HeroPill,
  cn,
} from '@tonnie/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { InOutTripFields } from '../forms/InOutTripFields';
import { CalculatorDiscardDialog, type CalculatorDiscard } from './CalculatorDiscardDialog';

import type { CalculatorWizard } from '../../hooks/use-calculator-wizard';
import { InOutClockList } from '../InOutClockList';
import { InOutAcquisitionCard, InOutRenewalCard } from '../InOutRecordCards';
import { InOutSimulationResult } from '../InOutSimulationResult';
import { InOutStatusBand } from '../InOutStatusBand';
import { InOutTimeline } from '../InOutTimeline';
import { INOUT_TONE, INOUT_CLASSES } from '../../lib/inout-tone';

/** How far ahead a trip to simulate can be picked. */

function WhatIfCard({ wizard }: { wizard: CalculatorWizard }) {
  const { t } = useLocale();
  const { control, handleSubmit } = useForm<InOutTripFormInput>({
    resolver: zodResolver(inoutTripFormSchema),
    defaultValues: { departsOn: '', returnsOn: '', destinationCountry: '' },
  });

  const onSimulate = handleSubmit((values) =>
    wizard.whatIf({
      departsOn: values.departsOn,
      returnsOn: values.returnsOn,
      destinationCountry: values.destinationCountry || null,
    })
  );

  return (
    <Card data-testid="inout-calculator-what-if">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plane className={cn('h-4 w-4', INOUT_CLASSES.text)} aria-hidden="true" />
          {t('inout.calculator.result.whatIfTitle')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t('inout.calculator.result.whatIfSubtitle')}
        </p>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={onSimulate} noValidate>
          <InOutTripFields
            control={control}
            names={{ from: 'departsOn', to: 'returnsOn', destination: 'destinationCountry' }}
            idPrefix="inout-calculator-what-if"
            when="planned"
          />
          <Button
            type="submit"
            variant="outline"
            loading={wizard.simulating}
            className="self-start"
            data-testid="inout-calculator-what-if-simulate"
          >
            {t('inout.planner.simulate')}
          </Button>
        </form>
        {wizard.tripProblem && (
          <p role="alert" className="mt-4 text-sm text-destructive-accessible">
            {wizard.tripProblem}
          </p>
        )}
        {wizard.simulation && wizard.answer && (
          <div className="mt-4">
            <InOutSimulationResult answer={wizard.simulation} before={wizard.answer} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** What keeping the answer says, in the host's words — and whether it is on its way. */
export interface CalculatorKeepCopy {
  title: string;
  why: string;
  label: string;
  busy?: boolean;
  /** The host refused to keep it — said as what it is, an error, under the why. */
  problem?: string | null;
}

export function CalculatorResult({
  wizard,
  keep,
}: {
  wizard: CalculatorWizard;
  keep: CalculatorKeepCopy;
}) {
  const { t, formatDate } = useLocale();
  const countryName = useCountryName();
  const [asking, setAsking] = useState<CalculatorDiscard | null>(null);
  const { answer } = wizard;
  if (!answer) return null;

  const { evaluation } = answer;
  const margin = inoutDaysToConfirm(evaluation);
  const country = countryName(evaluation.country);

  return (
    <div className="flex flex-col gap-6" data-testid="inout-calculator-result">
      <InOutStatusBand
        reading={answer}
        settleHref={null}
        eyebrow={
          <>
            <HeroPill>
              <CountryFlag code={evaluation.country} size="sm" />
              {country}
            </HeroPill>
            <span className="text-xs font-medium text-white/90">
              {t('inout.calculator.result.asOf', {
                date: formatDate(evaluation.evaluatedOn, 'calendarDay'),
              })}
            </span>
          </>
        }
      />

      <section
        className={cn(
          'flex flex-col gap-4 rounded-xl border p-5 @xl:flex-row @xl:items-center',
          INOUT_CLASSES.line,
          INOUT_CLASSES.subtle
        )}
        aria-labelledby="inout-calculator-keep-title"
      >
        <div className="min-w-0 flex-1">
          <h3 id="inout-calculator-keep-title" className="text-base font-semibold text-foreground">
            {keep.title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{keep.why}</p>
          {keep.problem ? (
            <p
              className="mt-2 text-sm font-medium text-destructive-accessible"
              role="alert"
              data-testid="inout-calculator-keep-problem"
            >
              {keep.problem}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          onClick={wizard.keep}
          loading={keep.busy}
          tone={INOUT_TONE}
          className="h-11 shrink-0 gap-2"
          data-testid="inout-calculator-keep"
        >
          <Bookmark className="h-4 w-4" aria-hidden="true" />
          {keep.label}
        </Button>
      </section>

      {!wizard.restInHost && margin > 0 && (
        <Alert variant="warning" role="note" data-testid="inout-calculator-uncertain">
          <CircleDot aria-hidden="true" />
          <AlertDescription>
            {t('inout.calculator.result.uncertainBetween', { country })}
          </AlertDescription>
          <AlertAction>
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={wizard.calculating}
              onClick={wizard.countRestInHost}
              data-testid="inout-calculator-count-rest"
            >
              {t('inout.calculator.result.countRest', { country })}
            </Button>
          </AlertAction>
        </Alert>
      )}

      <InOutTimeline
        status={answer}
        absences={wizard.trips.map(({ key, departedOn, returnedOn }) => ({
          id: key,
          departedOn,
          returnedOn,
        }))}
      />

      <InOutClockList status={answer} settleHref={null} disclaimer={false} />

      <div className="grid items-start gap-6 @xl:grid-cols-2 @xl:[&>*:only-child]:col-span-2">
        <InOutRenewalCard status={answer} />
        <InOutAcquisitionCard status={answer} />
      </div>

      <WhatIfCard wizard={wizard} />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => wizard.goTo('trips')}
          data-testid="inout-calculator-change"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('inout.calculator.result.change')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="gap-2 text-muted-foreground"
          onClick={() => setAsking('restart')}
          data-testid="inout-calculator-restart"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {t('inout.calculator.result.restart')}
        </Button>
      </div>
      <CalculatorDiscardDialog
        asking={asking}
        onKeep={() => setAsking(null)}
        onDiscard={() => {
          setAsking(null);
          wizard.restart();
        }}
      />
    </div>
  );
}
